import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2, Package, Wrench, Factory, CheckCircle2, Truck, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { subcontractAPI, contractsAPI } from '@/lib/api';
import { EntityLink } from '@/components/EntityLink';

const STAGE_CONFIG = {
  rm_pending: { label: 'RM Pending', icon: Clock, color: 'border-t-amber-400 bg-amber-50/30', badge: 'bg-amber-100 text-amber-700', count_color: 'text-amber-600' },
  rm_received: { label: 'RM Received', icon: Package, color: 'border-t-blue-400 bg-blue-50/30', badge: 'bg-blue-100 text-blue-700', count_color: 'text-blue-600' },
  wo_in_progress: { label: 'WO In Progress', icon: Wrench, color: 'border-t-violet-400 bg-violet-50/30', badge: 'bg-violet-100 text-violet-700', count_color: 'text-violet-600' },
  production_done: { label: 'Production Done', icon: Factory, color: 'border-t-teal-400 bg-teal-50/30', badge: 'bg-teal-100 text-teal-700', count_color: 'text-teal-600' },
  ready_dispatch: { label: 'Ready to Dispatch', icon: CheckCircle2, color: 'border-t-emerald-400 bg-emerald-50/30', badge: 'bg-emerald-100 text-emerald-700', count_color: 'text-emerald-600' },
  dispatched: { label: 'Dispatched', icon: Truck, color: 'border-t-slate-400 bg-slate-50/30', badge: 'bg-slate-100 text-slate-600', count_color: 'text-slate-500' },
};

const STAGE_ORDER = ['rm_pending', 'rm_received', 'wo_in_progress', 'production_done', 'ready_dispatch', 'dispatched'];

function ChallanCard({ card, onClick }) {
  const totalWO = card.work_orders?.length || 0;
  const completedWO = card.work_orders?.filter(w => w.status === 'completed').length || 0;
  return (
    <div className="p-2.5 bg-white rounded-lg border hover:shadow-md transition-shadow cursor-pointer" onClick={() => onClick(card)} data-testid={`jw-card-${card.challan_number}`}>
      <div className="flex items-start justify-between mb-1.5">
        <span className="text-[11px] font-semibold font-mono">{card.challan_number}</span>
        {card.due_date && <span className="text-[9px] text-muted-foreground">{card.due_date}</span>}
      </div>
      <p className="text-[10px] text-muted-foreground truncate mb-1.5">{card.customer_name}</p>
      <div className="flex gap-1.5 flex-wrap">
        {card.rm_items?.slice(0, 2).map((rm, i) => (
          <Badge key={i} variant="outline" className="text-[9px] h-4 px-1">{rm.name?.slice(0, 15)}</Badge>
        ))}
      </div>
      <div className="flex items-center justify-between mt-2 pt-1.5 border-t">
        <span className="text-[9px] text-muted-foreground">RM: {card.received_rm_qty}/{card.total_rm_qty}</span>
        {totalWO > 0 && <span className="text-[9px] text-muted-foreground">WO: {completedWO}/{totalWO}</span>}
        {card.dispatched_qty > 0 && <span className="text-[9px] text-emerald-600">Sent: {card.dispatched_qty}</span>}
      </div>
    </div>
  );
}

export default function JobWorkDashboard() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ stages: {}, summary: {} });
  const [customers, setCustomers] = useState([]);
  const [customerFilter, setCustomerFilter] = useState('all');
  const [detailCard, setDetailCard] = useState(null);

  useEffect(() => {
    loadDashboard();
    loadCustomers();
  }, [customerFilter]);

  const loadCustomers = async () => {
    try {
      const res = await contractsAPI.activeCustomers();
      setCustomers(res.data || []);
    } catch { /* ignore */ }
  };

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const params = {};
      if (customerFilter && customerFilter !== 'all') params.customer_id = customerFilter;
      const res = await subcontractAPI.getJobWorkDashboard(params);
      setData(res.data || { stages: {}, summary: {} });
    } catch { toast.error('Failed to load dashboard'); }
    finally { setLoading(false); }
  };

  const summary = data.summary || {};

  return (
    <div className="space-y-4" data-testid="job-work-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold tracking-tight">Job Work Tracker</h1>
          <p className="text-xs text-muted-foreground">{summary.total_challans || 0} inbound challans tracked</p>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={customerFilter} onValueChange={v => setCustomerFilter(v)}>
            <SelectTrigger className="h-8 text-xs w-48" data-testid="jw-customer-filter">
              <SelectValue placeholder="All Customers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Customers</SelectItem>
              {customers.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.customer_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-6 gap-2">
        {STAGE_ORDER.map(key => {
          const cfg = STAGE_CONFIG[key];
          const Icon = cfg.icon;
          return (
            <Card key={key} className={`border-t-2 ${cfg.color}`}>
              <CardContent className="p-3 text-center">
                <Icon className={`w-4 h-4 mx-auto mb-1 ${cfg.count_color}`} />
                <div className={`text-xl font-bold ${cfg.count_color}`}>{summary[key] || 0}</div>
                <p className="text-[10px] text-muted-foreground">{cfg.label}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        /* Kanban Columns */
        <div className="grid grid-cols-6 gap-2 min-h-[400px]">
          {STAGE_ORDER.map(key => {
            const cfg = STAGE_CONFIG[key];
            const Icon = cfg.icon;
            const cards = data.stages?.[key] || [];
            return (
              <div key={key} className={`rounded-lg border-t-2 ${cfg.color} border p-2`}>
                <div className="flex items-center gap-1.5 mb-2 px-1">
                  <Icon className={`w-3.5 h-3.5 ${cfg.count_color}`} />
                  <span className="text-[11px] font-semibold">{cfg.label}</span>
                  <Badge className={`${cfg.badge} text-[9px] h-4 px-1 ml-auto`}>{cards.length}</Badge>
                </div>
                <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-0.5">
                  {cards.length === 0 && (
                    <p className="text-[10px] text-muted-foreground text-center py-4">No challans</p>
                  )}
                  {cards.map(card => (
                    <ChallanCard key={card.challan_id} card={card} onClick={setDetailCard} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!detailCard} onOpenChange={v => !v && setDetailCard(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          {detailCard && (
            <>
              <DialogHeader>
                <DialogTitle className="text-sm">{detailCard.challan_number}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-xs">
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-muted-foreground">Customer:</span> <strong>{detailCard.customer_name}</strong></div>
                  <div><span className="text-muted-foreground">Created:</span> {detailCard.created_at}</div>
                  <div><span className="text-muted-foreground">Due:</span> {detailCard.due_date || '-'}</div>
                  <div><span className="text-muted-foreground">Status:</span> <Badge variant="outline" className="text-[9px]">{detailCard.status}</Badge></div>
                </div>

                <div>
                  <h4 className="font-semibold mb-1">Raw Materials</h4>
                  <div className="space-y-1">
                    {detailCard.rm_items?.map((rm, i) => (
                      <div key={i} className="flex justify-between p-1.5 bg-muted/50 rounded">
                        <span>{rm.name}</span>
                        <span className="font-mono">{rm.received}/{rm.qty}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {detailCard.work_orders?.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-1">Work Orders</h4>
                    <div className="space-y-1">
                      {detailCard.work_orders.map((wo, i) => (
                        <div key={i} className="flex justify-between items-center p-1.5 bg-muted/50 rounded">
                          <div>
                            <span className="font-mono">{wo.wo_number}</span> - <EntityLink type="item" id={wo.item_id}>{wo.item_name}</EntityLink>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono">{wo.produced}/{wo.quantity}</span>
                            <Badge variant="outline" className={`text-[9px] ${wo.status === 'completed' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                              {wo.status}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {detailCard.dispatches?.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-1">Dispatches</h4>
                    <div className="space-y-1">
                      {detailCard.dispatches.map((d, i) => (
                        <div key={i} className="flex justify-between p-1.5 bg-emerald-50 rounded">
                          <span>{d.number}</span>
                          <Badge variant="outline" className="text-[9px]">{d.status}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {detailCard.manifests?.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-1">Manifests</h4>
                    <div className="space-y-1">
                      {detailCard.manifests.map((m, i) => (
                        <div key={i} className="flex justify-between p-1.5 bg-blue-50 rounded">
                          <span>{m.number}</span>
                          <Badge variant="outline" className="text-[9px]">{m.status}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
